import React from 'react';
import Select, { Props as SelectProps } from 'react-select';
import SelectAsync, { Props as SelectAsyncProps } from 'react-select/async';

import { useTranslation } from 'src/shared/translation';

import { SelectShell } from './select-shell';
import { useSharedSelectProps } from './use-shared-select-props';

export type SelectInputProps<Options> = {
    label?: NonNullable<React.ReactNode>;
    id: string;
    placeholderCode?: string;
    noOptionsCode?: string;
    errorMessage?: React.ReactNode;
    className?: string;
    defaultValue?: any;
    userEvent?: string;
    overrideStyles?: {
        [k in 'control' | 'singleValue']?: {};
    };
} & Pick<
    SelectProps<Options>,
    | 'options'
    | 'value'
    | 'getOptionLabel'
    | 'getOptionValue'
    | 'onChange'
    | 'filterOption'
    | 'defaultValue'
    | 'isSearchable'
>;

export function SelectInput<Options>({
    label,
    placeholderCode,
    noOptionsCode,
    errorMessage,
    userEvent,
    overrideStyles,
    ...props
}: SelectInputProps<Options>) {
    const sharedSelectProps = useSharedSelectProps<Options>({
        placeholderCode: placeholderCode || '',
        noOptionsCode: noOptionsCode || '',
        hasError: Boolean(errorMessage),
        overrideStyles,
    });

    return (
        <SelectShell
            label={label}
            inputId={props.id}
            errorMessage={errorMessage}
            userEvent={userEvent}
        >
            <Select {...sharedSelectProps} {...props} />
        </SelectShell>
    );
}

export type SelectAsyncInputProps<Options> = SelectInputProps<Options> &
    Pick<
        SelectAsyncProps<Options, boolean>,
        'loadOptions' | 'cacheOptions' | 'noOptionsMessage'
    > & {
        loadingCode: string;
        noDropdownIndicator?: boolean;
    };

export function SelectAsyncInput<Options>({
    label,
    placeholderCode,
    loadingCode,
    noOptionsCode,
    noDropdownIndicator,
    errorMessage,
    userEvent,
    ...props
}: SelectAsyncInputProps<Options>) {
    const { t } = useTranslation();
    const sharedSelectProps = useSharedSelectProps<Options>({
        placeholderCode: placeholderCode || '',
        noOptionsCode: noOptionsCode || '',
        noDropdownIndicator,
        hasError: Boolean(errorMessage),
    });

    return (
        <SelectShell
            label={label}
            inputId={props.id}
            errorMessage={errorMessage}
            userEvent={userEvent}
        >
            <SelectAsync loadingMessage={() => t(loadingCode)} {...sharedSelectProps} {...props} />
        </SelectShell>
    );
}
